const REQUIRED_DOCUMENT_TYPE_IDS = {
  identity: [2, 6, 10, 11],
  resume: [4],
  immunization: [7],
  vulnerableSectorCheck: [15],
};

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "_");

const isVerifiedStatus = (value) => {
  const status =
    normalizeStatus(value);

  return [
    "verified",
    "approved",
    "valid",
    "current",
  ].includes(status);
};

const isRejectedStatus = (value) => {
  const status =
    normalizeStatus(value);

  return [
    "rejected",
    "declined",
    "denied",
  ].includes(status);
};

const isExpiredRecord = (
  record
) => {
  const expiryDate =
    record?.expiry_date ||
    record?.expiryDate;

  if (!expiryDate) {
    return false;
  }

  const parsedDate =
    new Date(expiryDate);

  if (
    Number.isNaN(
      parsedDate.getTime()
    )
  ) {
    return false;
  }

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  return (
    parsedDate.getTime() <
    today.getTime()
  );
};

const getDocumentTypeId = (
  document
) =>
  Number(
    document?.document_type_id ??
      document?.documentTypeId
  );

const findDocumentByTypeIds = (
  documents,
  documentTypeIds
) =>
  documents.find(
    (document) =>
      documentTypeIds.includes(
        getDocumentTypeId(
          document
        )
      )
  ) || null;

const getVerificationState = (
  record
) => {
  if (!record) {
    return "missing";
  }

  if (
    isExpiredRecord(record)
  ) {
    return "expired";
  }

  const status =
    record.verification_status ||
    record.verificationStatus ||
    record.verification ||
    record.status;

  if (
    isVerifiedStatus(status)
  ) {
    return "verified";
  }

  if (
    isRejectedStatus(status)
  ) {
    return "rejected";
  }

  return "pending";
};

const calculateProfessionalStatus = ({
  personalInformationComplete = false,
  specialties = [],
  licences = [],
  certifications = [],
  documents = [],
  availability = null,
}) => {
  const safeSpecialties =
    Array.isArray(specialties)
      ? specialties
      : [];

  const safeLicences =
    Array.isArray(licences)
      ? licences
      : [];

  const safeCertifications =
    Array.isArray(certifications)
      ? certifications
      : [];

  const safeDocuments =
    Array.isArray(documents)
      ? documents
      : [];

 const hasSelectedSpecialties =
  safeSpecialties.length > 0;

const hasPrimarySpecialty =
  safeSpecialties.some(
    (specialty) => {
      return (
        specialty.is_primary === true ||
        specialty.is_primary === 1 ||
        specialty.is_primary === "1" ||
        specialty.isPrimary === true ||
        specialty.isPrimary === 1 ||
        specialty.primary === true ||
        specialty.primary === 1
      );
    }
  );
    

const hasSpecialties =
  hasSelectedSpecialties &&
  hasPrimarySpecialty;

  const hasLicences =
    safeLicences.length > 0;

  const hasCredentials =
    hasLicences ||
    safeCertifications.length > 0 ||
    safeDocuments.length > 0;

  const hasAvailability =
    Boolean(
      availability &&
        (
          availability.is_configured ===
            true ||
          availability.isConfigured ===
            true ||
          availability.available ===
            true ||
          availability.status ===
            "active" ||
          availability.status ===
            "available" ||
          availability.days?.length >
            0 ||
          availability.availability
            ?.length > 0
        )
    );

  /*
  ========================================
  PROFILE SECTIONS
  ========================================
  */

  const profileSections = [
    {
      key:
        "personal-information",

      complete:
        personalInformationComplete,
    },

    {
      key:
        "clinical-specialties",

      complete:
        hasSpecialties,
    },

    {
      key:
        "credentials",

      complete:
        hasCredentials,
    },

    {
      key:
        "availability",

      complete:
        hasAvailability,
    },
  ];

  const completedProfileSections =
    profileSections.filter(
      (section) =>
        section.complete
    ).length;

  const profileSectionTotal =
    profileSections.length;

  /*
  ========================================
  REQUIRED CREDENTIALS
  ========================================
  */

  const identityDocument =
    findDocumentByTypeIds(
      safeDocuments,
      REQUIRED_DOCUMENT_TYPE_IDS
        .identity
    );

  const resumeDocument =
    findDocumentByTypeIds(
      safeDocuments,
      REQUIRED_DOCUMENT_TYPE_IDS
        .resume
    );

  const immunizationDocument =
    findDocumentByTypeIds(
      safeDocuments,
      REQUIRED_DOCUMENT_TYPE_IDS
        .immunization
    );

  const vulnerableSectorDocument =
    findDocumentByTypeIds(
      safeDocuments,
      REQUIRED_DOCUMENT_TYPE_IDS
        .vulnerableSectorCheck
    );

  const requiredRecords = [
    {
      key: "identity",
      record:
        identityDocument,
    },

    {
      key: "resume",
      record:
        resumeDocument,
    },

    ...safeLicences.map(
      (
        licence,
        index
      ) => ({
        key:
          `professional-licence-${
            licence.id ||
            index
          }`,

        record:
          licence,
      })
    ),

    {
      key:
        "immunization",

      record:
        immunizationDocument,
    },

    {
      key:
        "vulnerable-sector-check",

      record:
        vulnerableSectorDocument,
    },
  ];

  const verificationItems =
    requiredRecords.map(
      (item) => ({
        ...item,

        state:
          getVerificationState(
            item.record
          ),
      })
    );

  const verifiedCount =
    verificationItems.filter(
      (item) =>
        item.state ===
        "verified"
    ).length;

  const pendingCount =
    verificationItems.filter(
      (item) =>
        item.state ===
        "pending"
    ).length;

  const rejectedCount =
    verificationItems.filter(
      (item) =>
        item.state ===
        "rejected"
    ).length;

  const expiredCount =
    verificationItems.filter(
      (item) =>
        item.state ===
        "expired"
    ).length;

  const missingCount =
    verificationItems.filter(
      (item) =>
        item.state ===
        "missing"
    ).length;

  const verificationTotal =
    verificationItems.length;

  const verificationPercentage =
    verificationTotal > 0
      ? Math.round(
          (
            verifiedCount /
            verificationTotal
          ) * 100
        )
      : 0;

  /*
  ========================================
  OVERALL PROFILE COMPLETION
  ========================================

  A required credential counts as complete
  once it has been uploaded.

  Pending, verified, rejected and expired
  records are present.

  Missing records are incomplete.
  */

  const uploadedRequiredCredentials =
    verificationItems.filter(
      (item) =>
        item.state !==
        "missing"
    ).length;

  const totalCompletionItems =
    profileSectionTotal +
    verificationTotal;

  const completedCompletionItems =
    completedProfileSections +
    uploadedRequiredCredentials;

  const profilePercentage =
    totalCompletionItems > 0
      ? Math.round(
          (
            completedCompletionItems /
            totalCompletionItems
          ) * 100
        )
      : 0;

  const profileComplete =
    profilePercentage === 100 &&
    missingCount === 0;

  /*
  ========================================
  ASSIGNMENT READINESS
  ========================================
  */

  const allRequiredCredentialsVerified =
    verificationTotal > 0 &&
    verifiedCount ===
      verificationTotal;

  const assignmentReady =
    personalInformationComplete &&
    hasSpecialties &&
    hasAvailability &&
    profileComplete &&
    allRequiredCredentialsVerified;

  /*
  ========================================
  VERIFICATION LABEL
  ========================================
  */

  let verificationLabel =
    "Pending Review";

  if (
    rejectedCount > 0 ||
    expiredCount > 0
  ) {
    verificationLabel =
      "Action Required";
  } else if (
    missingCount > 0
  ) {
    verificationLabel =
      "Incomplete";
  } else if (
    allRequiredCredentialsVerified
  ) {
    verificationLabel =
      "Verified";
  }

  /*
  ========================================
  ASSIGNMENT LABEL
  ========================================
  */

  let assignmentLabel =
    "Not Assignment Ready";

  if (
    assignmentReady
  ) {
    assignmentLabel =
      "Assignment Ready";
  } else if (
    profileComplete &&
    missingCount === 0 &&
    (
      pendingCount > 0 ||
      rejectedCount > 0 ||
      expiredCount > 0
    )
  ) {
    assignmentLabel =
      rejectedCount > 0 ||
      expiredCount > 0
        ? "Credential Action Required"
        : "Awaiting Credential Verification";
  } else if (
    !profileComplete ||
    missingCount > 0
  ) {
    assignmentLabel =
      "Profile Incomplete";
  }

  return {
    profile: {
      percentage:
        profilePercentage,

      complete:
        profileComplete,

      completed:
        completedProfileSections,

      total:
        profileSectionTotal,

      remaining:
        profileSectionTotal -
        completedProfileSections,

      sections:
        profileSections,

      completedItems:
        completedCompletionItems,

      totalItems:
        totalCompletionItems,

      requiredUploaded:
        uploadedRequiredCredentials,

      requiredTotal:
        verificationTotal,

      requiredMissing:
        missingCount,
    },

    verification: {
      percentage:
        verificationPercentage,

      verified:
        verifiedCount,

      pending:
        pendingCount,

      rejected:
        rejectedCount,

      expired:
        expiredCount,

      missing:
        missingCount,

      total:
        verificationTotal,

      label:
        verificationLabel,

      items:
        verificationItems,
    },

    assignment: {
      ready:
        assignmentReady,

      label:
        assignmentLabel,
    },

    completion: {
      personalInformationComplete,
      hasSpecialties,
      hasCredentials,
      hasAvailability,
      profileComplete,
      allRequiredCredentialsVerified,
    },
  };
};

export default calculateProfessionalStatus;